/** @jsx tsx */
import { tsx } from "@dojo/framework/core/vdom";
import * as CONSTANTS from "../../Constants";
import { roundNumber } from "../../store/reducers/util";
import { IGeoBoundaryMeta } from "../interfaces/model/dataStructures/GeoBoundaryMeta";
import { IExtents } from "../interfaces/model/dataStructures/index";
import { IShapeGuide } from "../interfaces/model/geometry/ShapeGuide";
import { IXYAdjustHandle } from "../interfaces/model/geometry/XYAdjustHandle";
import { PageObjectType } from "../interfaces/model/Types";
import { CsiDrag, IDragResults } from "../meta/Drag/index";
import { getZoomValue } from "../selectors/index";
import { DraggableWidget } from "./DraggableWidget";

/** Adjust handler point render properites */
export interface IAdjustHandlerPointRenderProps {
    adjustHandler: IXYAdjustHandle;
    avList: IShapeGuide[];
    canvasContainerEl: HTMLElement;
    geoBoundaryMeta: IGeoBoundaryMeta;
    pageObject: PageObjectType;
    setShapeAdjustValues(dx: number, dy: number, adjustHandler: IXYAdjustHandle,
        avList: IShapeGuide[], refExt: IExtents, transient: boolean): void;
}

/**
 * Adjust handler point
 */
export class AdjustHandlerPoint extends DraggableWidget<IAdjustHandlerPointRenderProps>  {
	private _boundHandlers: any[];
	/**
     * Object to accumulate delta movements along x and y axis when pointer is out of editor area.
     * Object get initialized and maintained while drag is active on adjust handler.
     * It is set to undefined in all other cases.
     */
	private _outOfEditorDelta?: { x: number; y: number };

	constructor() {
		super();
		this._boundHandlers = [];
		/** Add event listeners for adjust handler */
		[
			{ event: "pointerup", func: this._onPointerUp.bind(this) },
		].forEach((object) => {
			document.addEventListener(object.event, object.func);
			this._boundHandlers.push(object);
		});
	}

	/**
     * Remove all pointer event listeners on shape
     */
	protected onDetach(): void {
		this._boundHandlers.forEach((object) => document.removeEventListener(object.event, object.func));
		const key = (this.properties.adjustHandler.gdRefX || this.properties.adjustHandler.gdRefY) + "_adjHandler";
		this.meta(CsiDrag)
			.remove(key);
	}

	/**
     * Pointer drag event for adjust handler
     * @param drag {DragResults}
     */
	protected onDrag(drag: IDragResults, isComplete: boolean) {
		let dx = drag.delta.x;
		let dy = drag.delta.y;
		if (drag.outOfEditor) {
			// if pointer is out of editor then
			// _outOfEditorDelta is not initialized then get one and accumulate deltas along x and y axis.
			if (!this._outOfEditorDelta) {
				this._outOfEditorDelta = { x: 0, y: 0 };
			}
			this._outOfEditorDelta.x += drag.delta.x;
			this._outOfEditorDelta.y += drag.delta.y;
			dx = 0;
			dy = 0;
		} else if (this._outOfEditorDelta) {
			// if pointer is not out of editor and if we had accumulated deltas when pointer was out of editor then
			// consider those and make _outOfEditorDelta undefined.
			dx += this._outOfEditorDelta.x;
			dy += this._outOfEditorDelta.y;
			this._outOfEditorDelta = undefined;
		}
		this.properties.setShapeAdjustValues(dx, dy, this.properties.adjustHandler,
			this.properties.avList,
			{ cx: this.properties.geoBoundaryMeta.width, cy: this.properties.geoBoundaryMeta.height },
			!isComplete);
	}

	/**
     * On adjust Handler pointer up reset outOfEditorDelta
     */
	protected _onPointerUp() {
		this._outOfEditorDelta = undefined;
	}

	protected render() {
		let { cx, cy } = this.properties.pageObject.spPr.xfrm.ext;
		const height = this.properties.geoBoundaryMeta.height || cy;
		const width = this.properties.geoBoundaryMeta.width || cx;
		const { locked } = this.properties.pageObject.spPr;
		// for Exact rendering of Adjust handler point on safari additional width and height is needed

		cx = Math.ceil(width) + CONSTANTS.ADDITIONAL_WIDTH + CONSTANTS.ADDITIONAL_DIMENSION_FACTOR;
		cy = Math.ceil(height) + CONSTANTS.ADDITIONAL_HEIGHT + CONSTANTS.ADDITIONAL_DIMENSION_FACTOR;

		let posX = this.properties.adjustHandler.pos.x as number;
		let posY = this.properties.adjustHandler.pos.y as number;

		// To handle "divide by zero" condition, we considered minimum dimension, iff cx or cy is zero.
		posX = (posX * CONSTANTS.PERCENTAGE_HUNDRED) / (cx || CONSTANTS.MIN_DIMENSION);
		posY = (posY * CONSTANTS.PERCENTAGE_HUNDRED) / (cy || CONSTANTS.MIN_DIMENSION);

		const key = (this.properties.adjustHandler.gdRefX || this.properties.adjustHandler.gdRefY) + "_adjHandler";

		if (!locked) {
			this.requestDragMeta(key);
		}
		return (
			<circle
				classes="c8_canvas_userSelectNone"
				cx={`${roundNumber(posX)}%`}
				cy={`${roundNumber(posY)}%`}
				fill="yellow"
				key={`${key}`}
				pointer-events="visible"
				r={`${getZoomValue(CONSTANTS.CIRCULAR_HANDLE_RADIUS)}`}
				stroke="black"
				stroke-width={`${getZoomValue(CONSTANTS.SELECTION_HANDLER_STROKE_WIDTH)}`}
				styles={{ cursor: !locked ? "crosshair" : "default" }}
				vector-effect="non-scaling-stroke"
			/>
		);
	}
}
